import { Injectable } from "@angular/core";
import {
  IOrderDataActionService,
  CHANNEL_ORDER_DATA_ACTION_SERVICE,
} from "../IOrderDataActionService";
import {
  PRIMITIVE,
  ClientMessageBrokerFactory,
  ClientMessageBroker,
  FnArg,
  UiArguments,
  FnTransferableArg,
} from "shared/provider-webworker";
import { BaseReplicaActionService } from "./BaseReplicaActionService";
import { Subject } from "rxjs";
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { FillOrderData } from "../../model/core/FillOrderData";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

@Injectable()
export class OrderDataActionReplicaService
  extends BaseReplicaActionService
  implements IOrderDataActionService
{
  protected broker: ClientMessageBroker;
  protected NEW_ORDER_REQUEST: CubiqTS.ConcreteEncodable;
  protected MODIFY_ORDER_REQUEST: CubiqTS.ConcreteEncodable;
  protected MODIFY_INFO_REQUEST: CubiqTS.ConcreteEncodable;
  protected MODIFY_RESPONSE: CubiqTS.ConcreteEncodable;
  protected AMEND_MARKUP_LOCK_RESPONSE: CubiqTS.ConcreteEncodable;
  protected AMEND_MARKUP_APPROVAL_REQUEST: CubiqTS.ConcreteEncodable;

  protected PAUSE_RESUME_REQUEST: CubiqTS.ConcreteEncodable;

  protected NEW_BMARK_REQUEST: CubiqTS.ConcreteEncodable;
  protected MODIFY_BMARK_REQUEST: CubiqTS.ConcreteEncodable;

  public modelNotificationFailureEventSubject: Subject<any> = new Subject<any>();
  protected clientRefreshPort: MessagePort = null;

  constructor(protected factory: ClientMessageBrokerFactory) {
    super();
    this.broker = this.factory.createMessageBroker(CHANNEL_ORDER_DATA_ACTION_SERVICE, true, true);
    this.NEW_ORDER_REQUEST = CubiqTS.ENCODABLE(CubiqTS.LinkedOrderRequestImpl);
    this.MODIFY_ORDER_REQUEST = CubiqTS.ENCODABLE(CubiqTS.LinkedOrderModificationRequestImpl);
    this.MODIFY_INFO_REQUEST = CubiqTS.ENCODABLE(CubiqTS.LinkedOrderInfoModificationRequestImpl);
    this.MODIFY_RESPONSE = CubiqTS.ENCODABLE(CubiqTS.StrategyModificationLockResponseImpl);
    this.AMEND_MARKUP_LOCK_RESPONSE = CubiqTS.ENCODABLE(
      CubiqTS.StrategyAmendModificationLockResponseImpl,
    );
    this.AMEND_MARKUP_APPROVAL_REQUEST = CubiqTS.ENCODABLE(
      CubiqTS.AmendMarkupSalesApprovalRequestImpl,
    );

    this.PAUSE_RESUME_REQUEST = CubiqTS.ENCODABLE(CubiqTS.OrderPauseResumeRequestImpl);

    this.NEW_BMARK_REQUEST = CubiqTS.ENCODABLE(CubiqTS.FixingOrderRequestImpl);
    this.MODIFY_BMARK_REQUEST = CubiqTS.ENCODABLE(CubiqTS.FixingOrderModificationRequestImpl);

    this.initModelUpdateService(this.broker, this.clientRefreshPort);
  }

  quickFillOrders(
    orderIds: Array<string>,
    stpRequirement: CubiqTS.FillStpRequirement,
  ): Promise<any> {
    let request = new UiArguments("quickFillOrders");
    let arg1 = new FnArg(orderIds, PRIMITIVE);
    let arg2 = new FnArg(stpRequirement, PRIMITIVE);
    request.args = [arg1, arg2];

    return this.broker.runOnService(request, PRIMITIVE);
  }

  cancelOrders(orderIds: Map<string, string[]>, reason: string): Promise<any> {
    let request = new UiArguments("cancelOrders");
    let arg1 = new FnArg(orderIds, PRIMITIVE);
    let arg2 = new FnArg(reason, PRIMITIVE);
    request.args = [arg1, arg2];

    return this.broker.runOnService(request, PRIMITIVE);
  }

  reactivateOrders(orderIds: Array<string>, fromDetailFill: boolean = false): Promise<any> {
    let request = new UiArguments("reactivateOrders");
    let arg1 = new FnArg(orderIds, PRIMITIVE);
    request.args = [arg1];

    return this.broker.runOnService(request, PRIMITIVE);
  }

  modifyOrderLock(orderId: string): Promise<any> {
    let request = new UiArguments("modifyOrderLock");
    let arg1 = new FnArg(orderId, PRIMITIVE);
    request.args = [arg1];

    return this.broker.runOnService(request, this.MODIFY_RESPONSE);
  }

  modifyOrdersUnlock(orderIds: Array<string>): Promise<any> {
    let request = new UiArguments("modifyOrdersUnlock");
    let arg1 = new FnArg(orderIds, PRIMITIVE);
    request.args = [arg1];

    return this.broker.runOnService(request, PRIMITIVE);
  }
  unlockBenchmarks(orderIds: Array<string>): Promise<any> {
    let request = new UiArguments("unlockBenchmarks");
    let arg1 = new FnArg(orderIds, PRIMITIVE);
    request.args = [arg1];

    return this.broker.runOnService(request, PRIMITIVE);
  }

  submitNewOrder(request: CubiqTS.LinkedOrderRequestImpl): Promise<any> {
    let req = new UiArguments("submitNewOrder");
    let arg1 = new FnArg(request, this.NEW_ORDER_REQUEST);
    req.args = [arg1];

    return this.broker.runOnService(req, PRIMITIVE);
  }

  submitModifiedOrder(request: CubiqTS.LinkedOrderModificationRequestImpl): Promise<any> {
    let req = new UiArguments("submitModifiedOrder");
    let arg1 = new FnArg(request, this.MODIFY_ORDER_REQUEST);
    req.args = [arg1];

    return this.broker.runOnService(req, PRIMITIVE);
  }

  submitModifyInfo(request: CubiqTS.LinkedOrderInfoModificationRequestImpl): Promise<any> {
    let req = new UiArguments("submitModifyInfo");
    let arg1 = new FnArg(request, this.MODIFY_INFO_REQUEST);
    req.args = [arg1];

    return this.broker.runOnService(req, PRIMITIVE);
  }

  lockOrdersForFill(orders: Array<CubiqTS.OrderDataImpl>): Promise<any> {
    let request = new UiArguments("lockOrdersForFill");
    let arg1 = new FnArg(orders, CubiqTS.ENCODABLE(CubiqTS.OrderDataImpl));
    request.args = [arg1];

    return this.broker.runOnService(request, PRIMITIVE);
  }

  unlockOrdersFromFill(orderIds: Array<string>): Promise<any> {
    let request = new UiArguments("unlockOrdersFromFill");
    let arg1 = new FnArg(orderIds, PRIMITIVE);
    request.args = [arg1];

    return this.broker.runOnService(request, PRIMITIVE);
  }

  detailFillOrders(fillOrders: Array<FillOrderData>): Promise<any> {
    let request = new UiArguments("detailFillOrders");
    let arg1 = new FnArg(fillOrders, PRIMITIVE);
    request.args = [arg1];

    return this.broker.runOnService(request, PRIMITIVE);
  }

  public releaseOrder(ids: Array<string>): Promise<void> {
    let request = new UiArguments("releaseOrder");
    request.args = [new FnArg(ids, PRIMITIVE)];

    return this.broker.runOnService(request, PRIMITIVE);
  }

  getModelNotificationFailureEventSubject(): Subject<CubiqTS.ConcreteEncodable> {
    return this.modelNotificationFailureEventSubject;
  }

  amendMarkupOrderLock(orderId: string): Promise<any> {
    let request = new UiArguments("amendMarkupOrderLock");
    let arg1 = new FnArg(orderId, PRIMITIVE);
    request.args = [arg1];

    return this.broker.runOnService(request, this.AMEND_MARKUP_LOCK_RESPONSE);
  }

  amendMarkupApproveAction(requestData: CubiqTS.AmendMarkupSalesApprovalRequestImpl): Promise<any> {
    let request = new UiArguments("amendMarkupApproveAction");
    let arg1 = new FnArg(requestData, this.AMEND_MARKUP_APPROVAL_REQUEST);
    request.args = [arg1];

    return this.broker.runOnService(request, PRIMITIVE);
  }

  pauseResumeOrders(requestData: CubiqTS.OrderPauseResumeRequestImpl): Promise<any> {
    let request = new UiArguments("pauseResumeOrders");
    let arg1 = new FnArg(requestData, this.PAUSE_RESUME_REQUEST);
    request.args = [arg1];

    return this.broker.runOnService(request, PRIMITIVE);
  }

  submitBmarkOrder(request: CubiqTS.FixingOrderRequestImpl): Promise<any> {
    let req = new UiArguments("submitBmarkOrder");
    let arg1 = new FnArg(request, this.NEW_BMARK_REQUEST);
    req.args = [arg1];

    return this.broker.runOnService(req, PRIMITIVE);
  }

  submitBmarkOrderModify(request: CubiqTS.FixingOrderModificationRequest): Promise<any> {
    let req = new UiArguments("submitBmarkOrderModify");
    let arg1 = new FnArg(request, this.MODIFY_BMARK_REQUEST);
    req.args = [arg1];

    return this.broker.runOnService(req, PRIMITIVE);
  }

  exportOrders(
    orderIds: string[],
    exportMarkup: boolean,
  ): Promise<CubiqTS.OrderExportResponseImpl> {
    return null;
  }

  toggleInflightInclusionState(
    orderIds: CubiqTS.Long[],
    state: CubiqTS.InflightInclusionState,
  ): Promise<CubiqTS.ToggleInflightStateResult> {
    return null;
  }
}
